/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from "react";
import { Skeleton } from "primereact/skeleton";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { accessAPI } from "../../utils";

// El worker de pdf.js corre las operaciones pesadas en un Web Worker. Lo
// servimos desde jsDelivr para no tener que configurar el bundle del worker
// en Webpack/CRA. La versión coincide con la del paquete instalado.
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.js`;

/**
 * Renderiza un PDF inline en un canvas usando pdf.js. Mantiene control total
 * del fondo (siempre blanco, sin el chrome del viewer nativo del navegador).
 *
 * Soporta dos fuentes:
 *  - `endpoint`: ruta de la API protegida (se descarga con accessAPI usando el
 *    Bearer token).
 *  - `url`: URL pública directa.
 *
 * @param {string} [props.endpoint] - Ruta de la API que devuelve un PDF (auth).
 * @param {string} [props.url] - URL pública directa de un PDF.
 * @param {string|number} [props.height="500px"] - Alto del contenedor.
 * @param {string|number} [props.width="100%"] - Ancho del contenedor.
 * @param {number} [props.pagina=1] - Número de página a renderizar (1-based).
 */
export default function PdfInline({
  endpoint,
  url,
  height = "500px",
  width = "100%",
  pagina = 1,
  sinMarco,
}) {
  const altoAuto = height === "auto" || height == null;
  const canvasRef = useRef(null);
  const contenedorRef = useRef(null);
  const [loader, setLoader] = useState(true);
  const [error, setError] = useState(null);

  // Renderiza el PDF al canvas: ajusta el viewport para que la página entre
  // en el contenedor y multiplica por devicePixelRatio para que se vea nítida
  // en pantallas Retina.
  // - Si hay un alto fijo, se escala para que entre completa en el contenedor
  //   (manteniendo la relación de aspecto).
  // - Si el alto es "auto", se escala solo por ancho y el alto del canvas es
  //   el que dicta el aspect ratio del PDF — el contenedor crece con él.
  async function renderizar(arrayBuffer) {
    const canvas = canvasRef.current;
    const contenedor = contenedorRef.current;
    if (!canvas || !contenedor) return;

    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const numeroPagina = Math.min(Math.max(pagina, 1), pdf.numPages);
    const page = await pdf.getPage(numeroPagina);

    const viewportSinEscala = page.getViewport({ scale: 1 });
    const escalaAncho = contenedor.clientWidth / viewportSinEscala.width;
    const escalaLogica =
      altoAuto || !contenedor.clientHeight
        ? escalaAncho
        : Math.min(
            escalaAncho,
            contenedor.clientHeight / viewportSinEscala.height,
          );
    const dpr = window.devicePixelRatio || 1;

    const viewport = page.getViewport({ scale: escalaLogica * dpr });
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = `${viewport.width / dpr}px`;
    canvas.style.height = `${viewport.height / dpr}px`;

    const ctx = canvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport }).promise;
  }

  useEffect(() => {
    let cancelado = false;

    async function cargar() {
      setLoader(true);
      setError(null);
      try {
        let arrayBuffer;
        if (url) {
          const resp = await fetch(url);
          if (!resp.ok) throw new Error("Error al descargar el PDF");
          arrayBuffer = await resp.arrayBuffer();
        } else if (endpoint) {
          arrayBuffer = await new Promise((resolve, reject) => {
            accessAPI(
              "GET",
              endpoint,
              null,
              (blob) =>
                blob
                  .arrayBuffer()
                  .then(resolve, () => reject(new Error("Error al leer PDF"))),
              (resp) =>
                reject(new Error(resp?.[0]?.msg || "Error al cargar el PDF")),
              null,
              true,
            );
          });
        } else {
          return;
        }
        if (cancelado) return;
        await renderizar(arrayBuffer);
        if (!cancelado) setLoader(false);
      } catch (e) {
        if (!cancelado) {
          setError(e?.message || "Error al cargar el PDF");
          setLoader(false);
        }
      }
    }

    cargar();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, url, pagina]);

  return (
    <div
      ref={contenedorRef}
      css={{
        width,
        height: altoAuto ? undefined : height,
        border: sinMarco ? "none" : "1px solid #ddd",
        borderRadius: sinMarco ? 0 : "6px",
        background: sinMarco ? "transparent" : "white",
        display: "flex",
        // En modo alto auto el contenedor se ajusta al canvas y queda alineado
        // hacia arriba; con alto fijo se centra vertical/horizontalmente.
        alignItems: altoAuto ? "flex-start" : "center",
        justifyContent: "center",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {loader && !error && (
        <Skeleton width="80%" height={altoAuto ? "200px" : "80%"} />
      )}
      {error && (
        <div
          css={{
            color: "var(--rojo)",
            padding: "1em",
            textAlign: "center",
          }}
        >
          {error}
        </div>
      )}
      <canvas
        ref={canvasRef}
        css={{
          display: error || loader ? "none" : "block",
          maxWidth: "100%",
          maxHeight: "100%",
        }}
      />
    </div>
  );
}
